import { CalendarRange, Check, X } from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { useLeaves, useLeaveMutations } from '@/features/hr/hooks/useHr';

export const pendingApprovalsConfig: WidgetConfig = {
  id: 'dash-pending-approvals',
  title: 'Pending Approvals',
  category: 'action',
  description: 'Leave requests awaiting your review',
  refreshStrategy: 'interval',
  refreshIntervalMs: 120_000,
  defaultSize: { w: 6, h: 3 },
  priority: 2,
  supportedRoles: ['admin', 'superadmin'],
};

// Leaves not yet in a terminal state still need review action.
const OPEN_STATUSES = new Set(['pending', 'admin_approved']);

const TYPE_LABEL: Record<string, string> = {
  annual: 'Annual',
  sick: 'Sick',
  emergency: 'Emergency',
  unpaid: 'Unpaid',
  other: 'Other',
};

export function PendingApprovalsWidget() {
  const { data, isLoading, isError, error, refetch } = useLeaves();
  const { reviewLeave } = useLeaveMutations();

  const pending = (data ?? []).filter((l) => OPEN_STATUSES.has(l.status)).slice(0, 6);

  return (
    <WidgetContainer
      config={pendingApprovalsConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => {
        refetch();
      }}
      actions={
        pending.length > 0 ? (
          <Badge tone="warning">{pending.length}</Badge>
        ) : undefined
      }
    >
      {pending.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <CalendarRange className="h-6 w-6 text-on-surface-variant" />
          <p className="text-body-sm text-on-surface-variant">No requests awaiting review.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {pending.map((l) => {
            const busy = reviewLeave.isPending && reviewLeave.variables?.id === l.id;
            return (
              <li
                key={l.id}
                className="flex items-center justify-between gap-3 rounded-card-sm border border-outline bg-surface px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-data-md font-medium text-on-surface">
                      {l.employee_name ?? 'Employee'}
                    </p>
                    <Badge tone="neutral">{TYPE_LABEL[l.type] ?? l.type}</Badge>
                    {l.status === 'admin_approved' && <Badge tone="info">Stage 2</Badge>}
                  </div>
                  <p className="truncate text-body-sm text-on-surface-variant">
                    {l.from_date} → {l.to_date} · {l.days} day{l.days === 1 ? '' : 's'}
                    {l.employee_dept ? ` · ${l.employee_dept}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    loading={busy}
                    onClick={() => reviewLeave.mutate({ id: l.id, status: 'approved' })}
                    aria-label={`Approve leave for ${l.employee_name ?? 'employee'}`}
                  >
                    <Check className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => reviewLeave.mutate({ id: l.id, status: 'rejected' })}
                    aria-label={`Reject leave for ${l.employee_name ?? 'employee'}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetContainer>
  );
}
